import {
  Dialog,
  formatCurrency,
  formatDate,
  formatNumber
} from '@ijoin/components';
import { AnnuityCarrierTransactionRes } from '@ijoin/hooks';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography
} from '@mui/material';

import { FC } from 'react';

import { Field } from './Field';

const DATE = { format: 'YYYY-MM-DD', useUtc: true } as const;
const DATE_TIME = { format: 'YYYY-MM-DD HH:mm:ss', useUtc: true } as const;
const TIME = { format: 'HH:mm:ss', useUtc: true } as const;
const UNITS = { maximumFractionDigits: 6, minimumFractionDigits: 6 } as const;

export const CarrierTransactionDetailsDialog: FC<{
  onClose: () => void;
  transaction: AnnuityCarrierTransactionRes;
}> = props => (
  <Dialog
    data-testid='transactionDetails'
    fullWidth
    isOpen
    maxWidth='md'
    onClose={props.onClose}>
    <DialogTitle
      sx={{
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'space-between'
      }}>
      <Typography component='span' variant='h4'>
        {`Transaction Details - ID: ${props.transaction.id}`}
      </Typography>
      <IconButton data-testid='closeTransactionDetails' onClick={props.onClose}>
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent dividers>
      <Grid container spacing={2}>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Field label='ID' value={props.transaction.id} />
        </Grid>
        <Grid size={{ sm: 8, xs: 12 }}>
          <Field
            label='Plan Correlation ID'
            value={props.transaction.planCorrelationId}
          />
        </Grid>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Field
            label='External Plan ID'
            value={props.transaction.externalPlanId ?? ''}
          />
        </Grid>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Field
            label='Participant ID'
            value={props.transaction.participantId}
          />
        </Grid>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Field label='RK Account ID' value={props.transaction.rkAccountId} />
        </Grid>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Field
            label='External Source ID'
            value={props.transaction.externalSourceId ?? ''}
          />
        </Grid>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Field
            label='External Fund ID'
            value={props.transaction.externalFundId ?? ''}
          />
        </Grid>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Field
            label='Trans Type Code'
            value={props.transaction.transTypeCode ?? ''}
          />
        </Grid>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Field
            label='Transaction Type'
            value={props.transaction.transactionType ?? ''}
          />
        </Grid>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Field
            label='Post Date'
            value={formatDate(props.transaction.postDate, DATE)}
          />
        </Grid>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Field
            label='Post Time'
            value={formatDate(props.transaction.postTime, TIME)}
          />
        </Grid>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Field label='Seq Num' value={props.transaction.seqNum ?? ''} />
        </Grid>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Field
            label='Trans Effective Date'
            value={formatDate(props.transaction.transEffectiveDate, DATE_TIME)}
          />
        </Grid>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Field
            label='Number of Units'
            value={
              props.transaction.numberOfUnits == null
                ? ''
                : formatNumber(props.transaction.numberOfUnits, UNITS)
            }
          />
        </Grid>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Field
            label='Unit Value'
            value={
              props.transaction.unitValue == null
                ? ''
                : formatNumber(props.transaction.unitValue, UNITS)
            }
          />
        </Grid>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Field
            label='Trade Date'
            value={formatDate(props.transaction.tradeDate, DATE_TIME)}
          />
        </Grid>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Field
            label='Total Dollar Amt'
            value={
              props.transaction.totalDollarAmt == null
                ? ''
                : formatCurrency(props.transaction.totalDollarAmt)
            }
          />
        </Grid>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Field label='Trans ID' value={props.transaction.transId ?? ''} />
        </Grid>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Field label='Trade ID' value={props.transaction.tradeId ?? ''} />
        </Grid>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Field
            label='Status Code'
            value={props.transaction.statusCode ?? ''}
          />
        </Grid>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Field
            label='Trade Certainty Code'
            value={props.transaction.tradeCertaintyCode ?? ''}
          />
        </Grid>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Field
            label='Buy Sell Code'
            value={props.transaction.buySellCode ?? ''}
          />
        </Grid>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Field label='Ref Num' value={props.transaction.refNum ?? ''} />
        </Grid>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Field
            label='Reversal Indicator'
            value={props.transaction.reversalIndicator ? 'Yes' : 'No'}
          />
        </Grid>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Field
            label='Data Source Type'
            value={props.transaction.dataSourceType ?? ''}
          />
        </Grid>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Field
            label='Created At'
            value={formatDate(props.transaction.createdAt, DATE_TIME)}
          />
        </Grid>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Field
            label='Updated At'
            value={formatDate(props.transaction.updatedAt, DATE_TIME)}
          />
        </Grid>
      </Grid>
    </DialogContent>
    <DialogActions>
      <Button
        data-testid='closeTransactionDetailsFooter'
        onClick={props.onClose}
        variant='outlined'>
        Close
      </Button>
    </DialogActions>
  </Dialog>
);

CarrierTransactionDetailsDialog.displayName = 'CarrierTransactionDetailsDialog';
